/**
 * Modal de Troca de Plantão (GUPM-104) — alinhado ao protótipo v3 sc-modal.
 *
 * Layout (cima → baixo):
 *   [header]                                  ← fixo
 *   ┌── Operadores envolvidos (card amarelo) ┐← fixo
 *   │   Saindo (read-only) | Entrando (sel)  │
 *   └─────────────────────────────────────────┘
 *   [stepper X/N + obs status]               ← fixo
 *   ─── scroll começa aqui ───
 *   🔴 Sistemas de Alerta
 *     · item 1
 *     · item 2
 *     · item 3
 *   🟡 Ocorrências do Turno Anterior
 *     · item 4
 *     · item 5 (chuva + condicional)
 *     · item 6 (sensores)
 *   🟢 Status dos Instrumentos e Estrutura
 *     · item 7
 *     · item 8
 *     · item 9
 *   [observação]
 *   [card amarelo confirmação TOTP: saindo + entrante]
 *   ─── scroll termina ───
 *   [erro, quando houver]                    ← fixo (fora do scroll de propósito)
 *   [footer]                                 ← fixo
 */

import { useEffect, useMemo, useState } from 'react';
import {
  countItemsCompleted,
  divergentItems,
  hasObservation,
  isDraftComplete,
  observationRequired,
} from '@/domain/shift-handover/checklist-rules';
import type { ChecklistResponse } from '@/domain/shift-handover/types';
import {
  HandoverChecklist,
  HandoverHeader,
  HandoverObservacao,
  HandoverParticipantes,
  HandoverStepper,
  HandoverTotp,
  type ChecklistGroup,
} from './components/HandoverSections';
import {
  useActiveOperators,
  useChecklistCatalog,
  useCloseShift,
  useCreateShiftHandover,
  useOpenShift,
  type CurrentEngineer,
} from './hooks';
import { usePopup } from '@/hooks/usePopup';
import { useFecharComConfirmacao } from '@/hooks/useFecharComConfirmacao';
import { ConfirmarDescarte } from '@/components/ConfirmarDescarte';
import { useDamCode } from './useDamId';
import { SEM_SUBSTITUTO } from './constants';

/**
 * `abertura` = não há plantonista e quem preenche ASSUME (GUPM-272).
 * `troca`    = há plantonista e quem preenche PASSA o plantão (GUPM-104).
 *
 * A diferença não é cosmética: no modo abertura não existe "entrante" a
 * escolher — quem assume é sempre o usuário da sessão. O dropdown de entrante,
 * reaproveitado do modo troca, era o que punha o plantão no nome de terceiro.
 */
export type ShiftModalMode = 'abertura' | 'troca';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentEngineer: CurrentEngineer;
  /**
   * Barragem de registro da LINHA (`shift_handovers.dam_id` é NOT NULL e a
   * policy de INSERT confere `current_app_user_has_dam`). Opcional porque o
   * ENCERRAMENTO não escreve linha nova — e o plantão é global, cobre A, B e C.
   */
  damId?: string;
  mode?: ShiftModalMode;
}

export default function ShiftHandoverModal({
  isOpen,
  onClose,
  currentEngineer,
  damId,
  mode = 'troca',
}: Props) {
  const isAbertura = mode === 'abertura';
  const damCode = useDamCode();
  const { data: catalog = [], isLoading: catalogLoading } = useChecklistCatalog();
  const { data: operators = [], isLoading: operatorsLoading } = useActiveOperators(damCode);
  const trocaMutation = useCreateShiftHandover();
  const aberturaMutation = useOpenShift();
  const encerramentoMutation = useCloseShift();

  const [responses, setResponses] = useState<Record<string, ChecklistResponse>>({});
  const [operatorInId, setOperatorInId] = useState<string>('');
  const [observation, setObservation] = useState<string>('');
  const [saindoCode, setSaindoCode] = useState('');
  const [entranteCode, setEntranteCode] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  // Escolher "— Ninguém —" na lista converte a troca em encerramento: mesmo
  // checklist, um código só (não há entrante para confirmar) e motivo obrigatório.
  const isEncerramento = !isAbertura && operatorInId === SEM_SUBSTITUTO;
  const umCodigoSo = isAbertura || isEncerramento;
  const mutation = isAbertura
    ? aberturaMutation
    : isEncerramento
    ? encerramentoMutation
    : trocaMutation;

  useEffect(() => {
    if (isOpen) {
      setResponses({});
      setOperatorInId('');
      setObservation('');
      setSaindoCode('');
      setEntranteCode('');
      setSubmitAttempted(false);
      setTopError(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const draftResponses = useMemo(() => Object.values(responses), [responses]);

  const draft = useMemo(
    () => ({
      operator_out_id: currentEngineer.id,
      operator_in_id: operatorInId || null,
      // `isDraftComplete` só olha checklist e observação; a barragem entra aqui
      // por compatibilidade do tipo. Quem barra a falta dela é `canSubmit`.
      barragem_id: damId ?? '',
      responses: draftResponses,
      observation: observation || null,
    }),
    [currentEngineer.id, operatorInId, damId, draftResponses, observation],
  );

  const itemsCompleted = useMemo(
    () => countItemsCompleted(catalog, draftResponses),
    [catalog, draftResponses],
  );
  // GUPM-273: a abertura não pede checklist. Quem acabou de chegar não viu nada
  // para atestar — a atestação é do FIM do turno. Zerar aqui derruba junto o
  // passo do stepper, o "responda N itens restantes" e o bloqueio do botão.
  const totalItems = isAbertura ? 0 : catalog.length;

  const divergent = useMemo(
    () => divergentItems(catalog, draftResponses),
    [catalog, draftResponses],
  );
  const obsPorDivergencia = useMemo(
    () => observationRequired(catalog, draftResponses),
    [catalog, draftResponses],
  );
  // Abertura e encerramento sempre exigem observação — o banco recusa sem ela.
  // Sair da vigilância (ou entrar nela) sozinho é a exceção, e o motivo é o que
  // a fiscalização lê depois.
  const obsRequired = isAbertura || isEncerramento || obsPorDivergencia;
  const obsOk = !obsRequired || hasObservation(observation);
  const divergentLabel = useMemo(() => {
    if (divergent.length === 0) return '';
    const nums = divergent.map((i) => i.order_index).join(', ');
    return divergent.length === 1
      ? `Divergência no item ${nums}`
      : `Divergência nos itens ${nums}`;
  }, [divergent]);

  // GUPM-273: a abertura não tem checklist, então não há rascunho a completar —
  // o que ela exige é a observação, e disso cuida `obsOk`. Deixar
  // `isDraftComplete` aqui travaria o botão para sempre: o catálogo segue
  // carregado, nenhum item é renderizado, e nenhuma resposta chega ao rascunho.
  const draftOk = useMemo(
    () => (isAbertura ? true : isDraftComplete(catalog, draft)),
    [isAbertura, catalog, draft],
  );

  // Abertura e encerramento têm UM código (o de quem preenche); só a troca tem
  // dois, porque só nela existe alguém do outro lado para confirmar presença.
  // `saindoCode` é reaproveitado como "meu código" nos três modos — quem digita
  // é sempre o usuário da sessão.
  // GUPM-273: o plantão é GLOBAL — cobre as três barragens — e `dam_id` deixou
  // de ser NOT NULL. Nenhum dos três modos precisa de estrutura resolvida, então
  // sumiu daqui o gate que produzia "Barragem indisponível — recarregue a
  // página" enquanto o fetch ainda estava em curso.
  const canSubmit =
    draftOk &&
    obsOk &&
    /^\d{6}$/.test(saindoCode) &&
    !mutation.isPending &&
    (umCodigoSo || (operatorInId !== '' && /^\d{6}$/.test(entranteCode)));

  const operatorsExcludingMe = useMemo(
    () => operators.filter((o) => o.id !== currentEngineer.id),
    [operators, currentEngineer.id],
  );
  const entranteName = useMemo(
    () => operatorsExcludingMe.find((o) => o.id === operatorInId)?.name ?? '',
    [operatorsExcludingMe, operatorInId],
  );

  // Próxima ação pendente — guia o operador (label do botão quando disabled).
  // Ordem segue o fluxo natural do form (cima → baixo).
  const nextStepLabel: string | null = (() => {
    if (mutation.isPending) return 'Confirmando…';
    if (!isAbertura && !operatorInId) return 'Selecione o operador entrante';
    if (itemsCompleted < totalItems) {
      const restante = totalItems - itemsCompleted;
      // "itens", não "items" — o plural em inglês escapava aqui (frontend.md §8).
      return restante > 1 ? `Responda ${restante} itens restantes` : 'Responda 1 item restante';
    }
    if (!obsOk) return 'Descreva a observação obrigatória';
    if (!/^\d{6}$/.test(saindoCode)) {
      return umCodigoSo ? 'Informe o seu código' : 'Informe o código do saindo';
    }
    if (!umCodigoSo && !/^\d{6}$/.test(entranteCode)) return 'Informe o código do entrante';
    return null; // tudo pronto
  })();

  /** Rótulo do botão quando tudo está pronto — diz o que vai acontecer. */
  const acaoLabel = isAbertura
    ? 'Assumir Plantão'
    : isEncerramento
    ? 'Encerrar Plantão'
    : 'Confirmar Troca';

  // Agrupa catálogo por group_order preservando ordem
  const itemsByGroup = useMemo(() => {
    const groups: ChecklistGroup[] = [];
    for (const item of catalog) {
      let g = groups.find((x) => x.order === item.group_order);
      if (!g) {
        g = { order: item.group_order, label: item.group_label, color: item.group_color, items: [] };
        groups.push(g);
      }
      g.items.push(item);
    }
    groups.sort((a, b) => a.order - b.order);
    return groups;
  }, [catalog]);

  function setResponse(next: ChecklistResponse) {
    setResponses((prev) => ({ ...prev, [next.item_id]: next }));
    setTopError(null);
  }

  async function handleSubmit() {
    setSubmitAttempted(true);
    if (!draftOk) {
      const reasons: string[] = [];
      if (itemsCompleted < totalItems) reasons.push(`${totalItems - itemsCompleted} item(s) sem resposta`);
      if (!obsOk) reasons.push(`${divergentLabel.toLowerCase()} — observação obrigatória`);
      setTopError(`Existem pendências: ${reasons.join(' · ')}. Revise antes de confirmar.`);
      return;
    }
    if (!isAbertura && !operatorInId) {
      setTopError('Selecione o operador entrante.');
      return;
    }
    if (!/^\d{6}$/.test(saindoCode) || (!umCodigoSo && !/^\d{6}$/.test(entranteCode))) {
      setTopError('Códigos TOTP devem ter exatamente 6 dígitos.');
      return;
    }
    if (obsRequired && !hasObservation(observation)) {
      setTopError(
        isAbertura
          ? 'Descreva por que está assumindo o plantão sem passagem.'
          : isEncerramento
          ? 'Descreva por que está encerrando o plantão sem rendição.'
          : 'Descreva a observação obrigatória.',
      );
      return;
    }
    setTopError(null);
    try {
      if (isAbertura) {
        await aberturaMutation.mutateAsync({
          observation,
          code: saindoCode,
        });
      } else if (isEncerramento) {
        // Encerrar NÃO cria linha nova: é UPDATE na linha do plantão em curso.
        // O checklist vai junto — é a atestação de como a barragem ficou.
        await encerramentoMutation.mutateAsync({
          reason: observation,
          code: saindoCode,
          responses: draftResponses,
        });
      } else {
        await trocaMutation.mutateAsync({
          operatorInId,
          responses: draftResponses,
          observation: observation || null,
          saindoEngineerId: currentEngineer.id,
          saindoCode,
          entranteCode,
        });
      }
      onClose();
    } catch (err) {
      // PostgrestError não é instanceof Error, mas tem .message — surface ele.
      const msg =
        err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Falha inesperada ao confirmar.';
      // Log SANITIZADO — não vaza payload (que pode conter operator_id, etc).
      // Em produção, DevTools de qualquer user pode capturar este log.
      const op = isAbertura ? 'openShift' : isEncerramento ? 'closeShift' : 'createHandover';
      console.error(`[shift-handover] ${op} falhou:`, msg);
      setTopError(msg);
    }
  }

  // C-91: ×, Esc, "Fechar" e clique fora com algo preenchido perguntam antes de
  // descartar; enviando, nada fecha.
  // As três: trocar o entrante durante o envio troca a `mutation` de referência.
  const enviando = trocaMutation.isPending || aberturaMutation.isPending || encerramentoMutation.isPending;
  const saida = useFecharComConfirmacao({
    preenchido:
      Object.keys(responses).length > 0 || operatorInId !== '' || observation.trim() !== '' ||
      saindoCode !== '' || entranteCode !== '',
    ocupado: enviando,
    fechar: onClose,
  });
  const popupRef = usePopup<HTMLDivElement>({ aberto: isOpen, aoFechar: saida.pedirFechar, bloqueado: enviando });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) saida.pedirFechar();
      }}
    >
      <div
        ref={popupRef}
        role="dialog"
        aria-modal="true"
        aria-label={isAbertura ? 'Abertura de plantão' : isEncerramento ? 'Encerrar plantão' : 'Troca de plantão'}
        tabIndex={-1}
        className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-xl border-2 focus:outline-none border-jf-yellow/60 bg-jf-surface shadow-[0_0_40px_rgba(254,203,0,0.15)] overflow-hidden">
        <HandoverHeader
          isAbertura={isAbertura}
          isEncerramento={isEncerramento}
          onClose={saida.pedirFechar}
          fecharTravado={enviando}
        />

        <HandoverParticipantes
          isAbertura={isAbertura}
          myName={currentEngineer.name}
          operatorInId={operatorInId}
          onOperatorInChange={setOperatorInId}
          operators={operatorsExcludingMe}
          operatorsLoading={operatorsLoading}
          submitAttempted={submitAttempted}
        />

        <HandoverStepper
          itemsCompleted={itemsCompleted}
          totalItems={totalItems}
          obsRequired={obsRequired}
          obsOk={obsOk}
          divergentLabel={divergentLabel}
        />

        {/* ── Conteúdo scrollável ──────────────────────── */}
        <div className="sc-scroll flex-1 overflow-y-auto p-4 space-y-5">
          {catalogLoading && <p className="text-xs text-jf-muted">Carregando itens…</p>}

          {!isAbertura && (
            <HandoverChecklist
              groups={itemsByGroup}
              responses={responses}
              onChange={setResponse}
              submitAttempted={submitAttempted}
            />
          )}

          <HandoverObservacao
            isAbertura={isAbertura}
            isEncerramento={isEncerramento}
            obsRequired={obsRequired}
            obsPorDivergencia={obsPorDivergencia}
            obsOk={obsOk}
            submitAttempted={submitAttempted}
            divergentLabel={divergentLabel}
            divergentIndexes={divergent.map((d) => d.order_index)}
            value={observation}
            onChange={setObservation}
          />

          <HandoverTotp
            umCodigoSo={umCodigoSo}
            myName={currentEngineer.name}
            entranteName={entranteName}
            meuCodigo={saindoCode}
            onMeuCodigoChange={setSaindoCode}
            entranteCode={entranteCode}
            onEntranteCodeChange={setEntranteCode}
          />
        </div>

        {/* ── Erro (fixo, colado ao rodapé) ─────────────
            Fora do scroll de propósito: aqui dentro o alerta entrava como
            último filho, esticava o `scrollHeight` para além do que estava à
            vista e era coberto pelo rodapé. O operador clicava, não via nada,
            repetia — e cinco tentativas travam o TOTP por 15 min. Ao lado do
            botão que disparou a ação, ele aparece sempre. */}
        {topError && (
          <div
            role="alert"
            className="border-t border-red-500/60 bg-red-500/10 px-4 py-2 text-sm text-red-300"
          >
            {topError}
          </div>
        )}

        {/* ── Footer (fixo) ────────────────────────────── */}
        <footer className="flex items-center justify-between gap-3 border-t border-jf-border bg-jf-surface2/40 p-4">
          <button
            onClick={saida.pedirFechar}
            className="rounded-md border border-jf-border px-4 py-2 text-sm text-jf-muted hover:bg-jf-border"
            disabled={enviando}
          >
            Fechar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-bold transition ${
              !canSubmit
                ? 'cursor-not-allowed bg-jf-surface text-jf-dim border border-jf-border'
                : isEncerramento
                ? // Encerrar deixa a operação sem plantonista — o botão não pode
                  // ter a mesma cara de "seguir o fluxo normal".
                  'bg-red-500 text-white hover:brightness-110'
                : 'bg-jf-yellow text-black hover:brightness-110'
            }`}
          >
            {nextStepLabel ?? acaoLabel}
          </button>
        </footer>
        {saida.perguntando && <ConfirmarDescarte onContinuar={saida.continuar} onDescartar={saida.descartar} />}
      </div>
    </div>
  );
}

